/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { TouchableOpacity, View } from "react-native";
import { useMutation } from "@apollo/client";
import Icon from "react-icons-kit";
import { closeRound } from "react-icons-kit/ionicons/closeRound";
import Rodal from "rodal";

import { CREATE_CSV_SEND_EMAIL } from "graphql/internal/Mutations";
import { CreateCsvSendEmailRequest } from "graphql/internal/generated";

import { skaleBlack } from "config/UpduoColors";
import { Button, LoadingView, Text } from "UpduoLibrary";
import {
  formattedDateString,
  formattedReadableDateString,
} from "config/Utilities";
import * as UpduoAnalytics from "config/UpduoAnalytics";

interface CsvModalProps {
  visible: boolean;
  onClose: () => void;
  startDate?: Date;
  endDate?: Date;
  summitId?: string;
  exportType: string;
  onExport?: () => void;
}

export default function CsvModal({
  visible,
  onClose,
  startDate,
  endDate,
  summitId = "",
  exportType,
  onExport,
}: CsvModalProps) {
  const [sentTo, setSentTo] = useState<string | null>(null);

  const [
    createCsvSendEmail,
    { loading: mutationLoading, error: mutationError },
  ] = useMutation(CREATE_CSV_SEND_EMAIL, {
    onCompleted: ({ createCsvSendEmail }) => {
      if (!createCsvSendEmail.ok) {
        UpduoAnalytics.logEvent("app-onboarding-createcsvmutation-error", {
          error: createCsvSendEmail.reason,
        });
        console.error(
          "ERROR createCsvSendEmailMutation",
          createCsvSendEmail.reason
        );
      } else {
        setSentTo(createCsvSendEmail.recipient);
      }
    },
    onError: (error) => {
      UpduoAnalytics.logEvent("app-onboarding-createcsvmutation-error", {
        error,
      });
      console.error("ERROR createCsvSendEmailMutation", error);
    },
  });

  const _handleSendToEmailButton = async () => {
    UpduoAnalytics.logEvent("user-generate-csv", { exportType });
    try {
      const createCsvSendEmailRequest: CreateCsvSendEmailRequest = {
        startDate: startDate ? formattedDateString(startDate) : "",
        endDate: endDate ? formattedDateString(endDate) : "",
        summitId: summitId ? summitId : null,
        exportType,
      };
      await createCsvSendEmail({
        variables: {
          createCsvSendEmailRequest,
        },
      });
      onExport && onExport();
    } catch (err) {
      UpduoAnalytics.logEvent("user-generate-csv-error", {
        exportType,
        error: err,
      });
      console.error(err);
      console.error(mutationError);
    }
  };

  return (
    <Rodal
      visible={visible}
      onClose={() => {
        setSentTo(null);
        onClose();
      }}
      customStyles={{ borderRadius: 10 }}
    >
      <View
        style={{
          position: "absolute",
          top: 15,
          right: 15,
        }}
      >
        <TouchableOpacity
          onPress={() => {
            setSentTo(null);
            onClose();
          }}
        >
          <Text
            style={{
              color: skaleBlack,
            }}
          >
            <Icon size={20} icon={closeRound} />
          </Text>
        </TouchableOpacity>
      </View>
      <View
        style={{
          alignItems: "center",
          justifyContent: "space-around",
          padding: 20,
          height: "100%",
        }}
      >
        <Text isHeading={true} style={{ fontSize: 18 }}>
          {`Export CSV`}
        </Text>
        {startDate && endDate ? (
          <Text style={{ textAlign: "center" }}>
            {`Request ${exportType} data from\n`}
            <Text style={{ fontWeight: "bold" }}>
              {formattedReadableDateString(startDate)}
            </Text>
            {" to "}
            <Text style={{ fontWeight: "bold" }}>
              {formattedReadableDateString(endDate)}
            </Text>
            {"\nby clicking the button below."}
          </Text>
        ) : (
          // some export types don't require date range
          <Text style={{ textAlign: "center" }}>{`Request ${exportType.replace(
            "_",
            " "
          )} data export by clicking the button below.`}</Text>
        )}
        {mutationLoading ? (
          <LoadingView />
        ) : sentTo ? (
          <View style={{ alignItems: "center" }}>
            <Text
              style={{
                fontSize: 12,
                color: "green",
                textAlign: "center",
              }}
            >
              {"Data requested successfully!"}
              {`\n${
                exportType === "registered_members"
                  ? "Registered member"
                  : exportType === "unregistered_members"
                  ? "Unregistered members"
                  : exportType === "session"
                  ? "Session Count"
                  : exportType === "activity"
                  ? "Activity"
                  : "Topic Feedback"
              } data will be emailed to:\n`}
              <Text style={{ fontWeight: "bold" }}>{sentTo}</Text>
            </Text>
          </View>
        ) : (
          <Button
            onPress={_handleSendToEmailButton}
            text={"Send To Email"}
            marginTop={20}
          />
        )}
      </View>
    </Rodal>
  );
}
